import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, X, Flashlight } from 'lucide-react';
import { Button } from '../ui/Button';

interface CameraScannerProps {
  onScan: (barcode: string) => void;
  onClose: () => void;
  isActive: boolean;
}

export const CameraScanner: React.FC<CameraScannerProps> = ({
  onScan,
  onClose,
  isActive
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flashlight, setFlashlight] = useState(false);
  const [scanning, setScanning] = useState(false);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      setStream(mediaStream);
      setHasPermission(true);
      setError(null);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      setHasPermission(false);
      setError('Camera access denied or not available');
      console.error('Camera access error:', err);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const toggleFlashlight = async () => {
    if (stream) {
      const track = stream.getVideoTracks()[0];
      if (track && 'applyConstraints' in track) {
        try {
          await track.applyConstraints({
            advanced: [{ torch: !flashlight } as any]
          });
          setFlashlight(!flashlight);
        } catch (err) {
          console.error('Flashlight not supported:', err);
        }
      }
    }
  };

  // Mock barcode scanning (in production, use a proper barcode scanning library)
  const simulateScan = () => {
    if (scanning) return;
    
    setScanning(true);
    
    // Simulate scanning delay
    setTimeout(() => {
      const mockBarcodes = [
        '1234567890123',
        '9876543210987',
        'ABC123DEF456',
        'SKU-001-XYZ',
        'PROD-789-QRS'
      ];
      
      const randomBarcode = mockBarcodes[Math.floor(Math.random() * mockBarcodes.length)];
      onScan(randomBarcode);
      setScanning(false);
    }, 1500);
  };

  useEffect(() => {
    if (isActive && hasPermission === null) {
      startCamera();
    }

    return () => {
      if (!isActive) {
        stopCamera();
      }
    };
  }, [isActive]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  if (!isActive) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black"
    >
      <div className="relative w-full h-full">
        {/* Camera Controls */}
        <div className="absolute top-4 left-4 right-4 z-10 flex justify-between items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="bg-black/50 text-white hover:bg-black/70"
          >
            <X className="w-6 h-6" />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFlashlight}
            className="bg-black/50 text-white hover:bg-black/70"
          >
            <Flashlight className={`w-6 h-6 ${flashlight ? 'text-yellow-400' : ''}`} />
          </Button>
        </div>

        {/* Camera View */}
        <div className="relative w-full h-full">
          {hasPermission === null && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-white">
                <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Requesting camera access...</p>
              </div>
            </div>
          )}

          {hasPermission === false && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-white">
                <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="mb-4">Camera access is required for scanning</p>
                <Button onClick={startCamera} variant="primary">
                  Enable Camera
                </Button>
              </div>
            </div>
          )}

          {hasPermission && (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              
              {/* Scanner Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  {/* Scanning Frame */}
                  <div className="w-64 h-64 border-2 border-white rounded-lg relative">
                    {/* Corner markers */}
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-blue-500 rounded-tl-lg" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-blue-500 rounded-tr-lg" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-blue-500 rounded-bl-lg" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-blue-500 rounded-br-lg" />
                    
                    {/* Scanning line */}
                    {scanning && (
                      <motion.div
                        initial={{ y: -100 }}
                        animate={{ y: 264 }}
                        transition={{ duration: 1.5, ease: "linear" }}
                        className="absolute left-0 w-full h-1 bg-blue-500 shadow-lg"
                      />
                    )}
                  </div>
                  
                  <p className="text-white text-center mt-4">
                    {scanning ? 'Scanning...' : 'Position barcode within frame'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Manual Scan Button */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <Button
            onClick={simulateScan}
            disabled={scanning || !hasPermission}
            loading={scanning}
            size="lg"
            variant="scan"
          >
            {scanning ? 'Scanning...' : 'Scan Barcode'}
          </Button>
        </div>
      </div>
      
      <canvas ref={canvasRef} className="hidden" />
    </motion.div>
  );
};